import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, ArrowRight, CheckCircle, Clock, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const OFFICIAL_FLYER_URL = "https://customer-assets-cm19k8pv.emergentagent.net/job_e8d2b5f5-0cb7-4419-b7f1-41d20d77a12b/artifacts/f8dy9jim_IMG-20260820-WA0166.jpg";

const copy = {
  en: {
    back: "Back to Home",
    title: "Our Events",
    subtitle: "Discover our past and upcoming conferences, workshops, and gatherings.",
    upcoming: "Upcoming",
    past: "Past Events",
    noUpcoming: "No upcoming events are open right now. Check back soon!",
    noPast: "No past events yet.",
    register: "Register Now",
    ended: "Event Ended",
    loading: "Loading events…",
    theme: "Theme",
    dates: "Dates",
    venue: "Venue",
    fee: "Registration fee",
  },
  fr: {
    back: "Retour à l'Accueil",
    title: "Nos événements",
    subtitle: "Découvrez nos conférences, ateliers et rencontres passés et à venir.",
    upcoming: "À venir",
    past: "Événements passés",
    noUpcoming: "Aucun événement à venir pour le moment. Revenez bientôt !",
    noPast: "Aucun événement passé pour le moment.",
    register: "S'inscrire",
    ended: "Événement Terminé",
    loading: "Chargement des événements…",
    theme: "Thème",
    dates: "Dates",
    venue: "Lieu",
    fee: "Frais d'inscription",
  },
};

function formatDateRange(lang, start, end) {
  if (!start) return '';
  try {
    const s = new Date(start);
    const e = end ? new Date(end) : null;
    const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';
    const opts = { month: 'long', day: 'numeric', year: 'numeric' };
    if (!e) return s.toLocaleDateString(locale, opts);
    return `${s.toLocaleDateString(locale, opts)} → ${e.toLocaleDateString(locale, opts)}`;
  } catch (err) {
    return `${start} → ${end}`;
  }
}

export default function Events() {
  const { language } = useLanguage();
  const lang = language || 'fr';
  const t = copy[lang] || copy.fr;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/events`)
      .then(res => setData(res.data))
      .catch(() => setData({ events: [], current: null }))
      .finally(() => setLoading(false));
  }, []);

  const upcoming = (data?.events || []).filter(e => !e.is_past);
  const past = (data?.events || []).filter(e => e.is_past);

  return (
    <div data-testid="events-page" className="min-h-screen bg-[#fafafa]">
      {/* Header */}
      <div className="bg-[#1D1D1D] py-3 px-4">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_talitha-koumi/artifacts/xsd39lyf_IMG-20260121-WA0012.jpg"
              alt="TALITHA KOUMI TUG"
              className="h-10 w-auto"
            />
            <span className="text-white/60 text-xs font-bold tracking-widest hidden md:inline">TALITHA KOUMI TUG</span>
          </Link>
          <Link to="/" data-testid="events-back-home" className="text-white/70 hover:text-white flex items-center gap-1 text-sm transition-colors">
            <ArrowLeft className="w-4 h-4" /> {t.back}
          </Link>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#1D1D1D] via-[#2a2a2a] to-[#CD0000] text-white py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <p className="text-[#CD0000] font-bold text-xs tracking-widest mb-3 bg-white/10 inline-block px-3 py-1 rounded-full">
            {lang === 'fr' ? 'ÉVÉNEMENTS' : 'EVENTS'}
          </p>
          <h1 className="text-4xl md:text-6xl font-extrabold leading-tight mb-4">{t.title}</h1>
          <p className="text-white/70 text-lg max-w-2xl">{t.subtitle}</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {loading && <p className="text-gray-500 text-center py-12">{t.loading}</p>}

        {!loading && (
          <>
            {/* Upcoming */}
            <section className="mb-14">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#CD0000]/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-[#CD0000]" />
                </div>
                <h2 className="text-2xl font-extrabold text-[#1D1D1D]">{t.upcoming}</h2>
              </div>
              {upcoming.length === 0 ? (
                <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center text-gray-500">
                  {t.noUpcoming}
                </div>
              ) : (
                <div className="grid gap-5 md:grid-cols-2">
                  {upcoming.map(ev => (
                    <EventCard key={ev.id} ev={ev} lang={lang} t={t} isUpcoming />
                  ))}
                </div>
              )}
            </section>

            {/* Past */}
            <section>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-gray-500" />
                </div>
                <h2 className="text-2xl font-extrabold text-[#1D1D1D]">{t.past}</h2>
              </div>
              {past.length === 0 ? (
                <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center text-gray-500">
                  {t.noPast}
                </div>
              ) : (
                <div className="grid gap-5 md:grid-cols-2">
                  {past.map(ev => (
                    <EventCard key={ev.id} ev={ev} lang={lang} t={t} isUpcoming={false} />
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function EventCard({ ev, lang, t, isUpcoming }) {
  const title = lang === 'fr' ? (ev.title_fr || ev.title) : ev.title;
  const theme = lang === 'fr' ? ev.theme_fr : ev.theme_en;
  // Show the official flyer on the Ottawa 2026 upcoming event card
  const showFlyer = isUpcoming;
  return (
    <div
      data-testid={`event-card-${ev.id}`}
      className={`bg-white rounded-2xl border overflow-hidden transition-all hover:shadow-xl ${
        isUpcoming ? 'border-[#CD0000]/20 hover:border-[#CD0000]/60' : 'border-gray-100 opacity-75'
      }`}
    >
      {showFlyer && (
        <Link
          to="/conference-registration"
          data-testid={`event-flyer-${ev.id}`}
          className="block relative aspect-[4/5] bg-gradient-to-br from-[#F9E1EC] to-[#FCEEF5] overflow-hidden group"
        >
          <img
            src={OFFICIAL_FLYER_URL}
            alt={lang === 'fr' ? 'Affiche officielle de la Conférence Internationale 2027' : 'Official 2027 International Conference Flyer'}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent pointer-events-none" />
        </Link>
      )}
      <div className="p-6">
        <div className="flex items-start justify-between mb-3 gap-3">
          <h3 className="text-xl font-extrabold text-[#1D1D1D] leading-snug">{title}</h3>
          {!isUpcoming && (
            <span className="shrink-0 bg-gray-100 text-gray-500 text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wider">
              {t.ended}
            </span>
          )}
        </div>
        {theme && (
          <p className="text-[#CD0000] italic text-sm mb-4">« {theme} »</p>
        )}
        <div className="space-y-2 text-sm text-gray-600 mb-5">
          <div className="flex items-start gap-2">
            <Calendar className="w-4 h-4 shrink-0 mt-0.5 text-gray-400" />
            <span>{formatDateRange(lang, ev.start_date, ev.end_date)}</span>
          </div>
          {ev.venue && (
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 shrink-0 mt-0.5 text-gray-400" />
              <span>{ev.venue}</span>
            </div>
          )}
        </div>
        {isUpcoming && (
          <Link
            to="/conference-registration"
            data-testid={`event-register-${ev.id}`}
            className="inline-flex items-center gap-2 bg-[#CD0000] hover:bg-[#A00000] text-white font-bold py-2.5 px-5 rounded-xl text-sm transition-colors"
          >
            {t.register} <ArrowRight className="w-4 h-4" />
          </Link>
        )}
      </div>
    </div>
  );
}
